import React, { useEffect, useState } from "react";
import { UserDashboardService } from "../services/user/UserDashboardService";
import { Link } from "react-router-dom";
import { ShoppingBag, ChevronRight, Calendar, ExternalLink } from "lucide-react";
import UserDashboardLayout from "../components/user/UserDashboardLayout";
import OrderDetailsModal from "../components/user/OrderDetailsModal";
import SEO from "../components/SEO";

export default function UserDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchRecentOrders = async () => {
      try {
        const response = await UserDashboardService.getRecentOrders();
        if (response.status === "success") {
          setOrders(response.orders.slice(0, 5));
        }
      } catch (error) {
        console.error("Failed to fetch recent orders:", error);
        setError("Unable to load your recent orders at this time.");
      } finally {
        setLoading(false);
      }
    };

    fetchRecentOrders();
  }, []);

  const handleViewOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  return (
    <UserDashboardLayout>
      <SEO title="My Account Dashboard" url="/dashboard" />
      {/* Orders Section */}
      <section id="orders" className="bg-white rounded-2xl shadow-sm border border-black/5 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-serif italic text-black flex items-center gap-3">
            <ShoppingBag className="text-jazz-brown" />
            Recent Purchases
          </h2>
          {orders.length > 0 && (
            <Link
              to="/dashboard/purchase-history"
              className="text-[10px] tracking-[2px] uppercase font-bold text-jazz-brown hover:text-black transition-colors flex items-center gap-1"
            >
              View All
              <ChevronRight size={14} />
            </Link>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 py-4 border-b border-black/10 animate-pulse">
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-4 w-32 bg-gray-200 rounded" />
                <div className="h-4 w-16 bg-gray-200 rounded ml-auto" />
                <div className="h-4 w-16 bg-gray-200 rounded" />
                <div className="h-8 w-20 bg-gray-200 rounded-lg" />
              </div>
            ))}
          </div>
        ) : orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-black/10">
                  <th className="pb-4 text-[10px] tracking-[2px] uppercase text-black font-bold">Reference</th>
                  <th className="pb-4 text-[10px] tracking-[2px] uppercase text-black font-bold">Date</th>
                  <th className="pb-4 text-[10px] tracking-[2px] uppercase text-black font-bold text-right">Amount</th>
                  <th className="pb-4 text-[10px] tracking-[2px] uppercase text-black font-bold text-center">Status</th>
                  <th className="pb-4 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5">
                {orders.map((order) => (
                  <tr key={order.id} className="group hover:bg-black/[0.02] transition-colors">
                    <td className="py-4">
                      <span className="text-sm font-bold text-black font-mono">{order.reference}</span>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2 text-black/80">
                        <Calendar size={14} />
                        <span className="text-xs font-semibold">{new Date(order.created_at).toLocaleDateString()}</span>
                      </div>
                    </td>
                    <td className="py-4 text-right">
                      <span className="text-sm font-bold text-black">${order.amount.toFixed(2)}</span>
                    </td>
                    <td className="py-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-[9px] font-bold tracking-[1px] uppercase ${order.status === 'paid' || order.status === 'completed' ? 'bg-green-100 text-green-700' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          order.status === 'pending' || order.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                            order.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-black/5 text-black/80'
                        }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      <button
                        onClick={() => handleViewOrder(order.id)}
                        className="inline-flex py-2 px-4 bg-black text-white font-bold text-[9px] tracking-[1px] uppercase rounded-lg hover:bg-jazz-brown transition-colors gap-2 items-center"
                      >
                        <ExternalLink size={12} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 px-4 border-2 border-dashed border-black/10 rounded-xl">
            <div className="w-16 h-16 bg-black/5 rounded-full flex items-center justify-center mx-auto mb-4 text-black/60">
              <ShoppingBag size={24} />
            </div>
            <h3 className="text-base font-bold text-black mb-2">No orders yet</h3>
            <p className="text-xs text-black/80 max-w-sm mx-auto mb-6">
              You haven't placed any orders. When you buy music or merchandise, it will show up here.
            </p>
            <Link
              to="/shop"
              className="inline-flex py-3 px-6 bg-black text-white font-bold text-[10px] tracking-[2px] uppercase rounded-xl hover:bg-jazz-brown transition-colors"
            >
              Start Shopping
            </Link>
          </div>
        )}
      </section>

      <OrderDetailsModal
        orderId={selectedOrderId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fetchDetails={UserDashboardService.getOrderDetails}
      />
    </UserDashboardLayout>
  );
}